import React from 'react'
import chevronCircleDown24SVG from 'assets/images/chevron-circle-down-24.svg'
import chevronCircleRight24SVG from 'assets/images/chevron-circle-right-24.svg'
import navigationLeft16SVG from 'assets/images/navigation-left-16.svg'
import navigationRight16SVG from 'assets/images/navigation-right-16.svg'
import i18n from 'i18n'
import moment, { Moment } from 'moment'
import { useEffect, useRef, useState } from 'react'
import CustomTimeSelector from 'pages/calendar/components/EventEditor/CustomTimeSelector'
import { format12Hours } from 'pages/calendar/components/EventEditor/timeFormats'
import MonthGrid from 'pages/calendar/components/MonthGrid/index'
import MonthPicker from 'pages/calendar/components/MonthPicker/index'
import { DatePickerType, ViewMode, DatePickerTypeValue, ViewModeValue } from './DatePickerType'
import settings from 'popups/Settings'
import styles from './style.module.scss'
import { LIMIT_DATE } from 'pages/calendar/utils/DateUtils'

interface EditEventData {
  startDate: Date
  endDate: Date
  startTz: string
  endTz: string
  isMyEvent: boolean
  isAllDay: boolean
}

interface LimitDate {
  min?: Date
  max?: Date
  startDate?: Date
  endDate?: Date
}

interface CalendarDatePickerProps {
  pickerType?: DatePickerTypeValue
  value: Date | string | null
  navigationContainerType?: boolean
  useTodayMenu?: boolean
  disableExpand?: boolean
  useDialogStyle?: boolean
  hasEventIndexes?: Record<string, any> | null
  limitDate?: LimitDate
  onChange: (date: Date) => void
  onClose?: () => void
}

const CalendarDatePicker: React.FC<CalendarDatePickerProps> = ({
  pickerType = DatePickerType.DATE,
  value,
  navigationContainerType = false,
  useTodayMenu = true,
  disableExpand = true,
  useDialogStyle = false,
  hasEventIndexes = null,
  limitDate = LIMIT_DATE,
  onChange,
  onClose = () => {},
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(value ? new Date(value) : new Date())
  const [viewMode, setViewMode] = useState<ViewModeValue>(
    pickerType === DatePickerType.MONTH ? ViewMode.MONTH : ViewMode.DATE,
  )
  const [displayYear, setDisplayYear] = useState<number>(currentDate.getFullYear())
  const [displayMonth, setDisplayMonth] = useState<number>(currentDate.getMonth())
  const [selectedTime, setSelectedTime] = useState<string>(currentDate.toTimeString().slice(0, 5))
  const [expanded, setExpanded] = useState<boolean>(true)

  const focusedDate = value ? new Date(value) : new Date()
  const t = i18n.t

  const editEventData = useRef<EditEventData>({
    startDate: currentDate,
    endDate: currentDate,
    startTz: moment.tz.guess(),
    endTz: moment.tz.guess(),
    isMyEvent: true,
    isAllDay: false,
  })

  useEffect(() => {
    if (value) {
      const date = new Date(value)
      setCurrentDate(date)
      setDisplayYear(date.getFullYear())
      setDisplayMonth(date.getMonth())
      setSelectedTime(date.toTimeString().slice(0, 5))
    }
  }, [value])

  const handleCustomTimeChange = (timeStr: string): void => {
    console.log('handleCustomTimeChange called with:', timeStr)

    const [hours, minutes] = timeStr.split(':')
    const newDateTime = new Date(currentDate)
    newDateTime.setHours(parseInt(hours), parseInt(minutes))

    console.log('New datetime:', newDateTime)

    setCurrentDate(newDateTime)
    setSelectedTime(timeStr)
  }

  const handleDateClick = (date: Moment): void => {
    const selectedDate = date.toDate()
    setCurrentDate(selectedDate)

    if (pickerType === DatePickerType.DATETIME) {
      const [hours, minutes] = selectedTime.split(':')
      const dateTimeValue = new Date(selectedDate)
      dateTimeValue.setHours(parseInt(hours), parseInt(minutes))
      setCurrentDate(dateTimeValue)
    } else {
      onChange(selectedDate)
      onClose()
    }
  }

  const handleDateTimeConfirm = (): void => {
    onChange(currentDate)
    onClose()
  }

  const handleMonthClick = (monthDate: Date): void => {
    const month = monthDate.getMonth()
    setDisplayMonth(month)

    if (pickerType === DatePickerType.MONTH) {
      const monthValue = new Date(displayYear, month, 1)
      setCurrentDate(monthValue)
      onChange(monthValue)
      onClose()
    } else {
      setViewMode(ViewMode.DATE)
    }
  }

  const handleYearClick = (year: number): void => {
    setDisplayYear(year)
    setViewMode(ViewMode.MONTH)
  }

  const handlePrevious = (): void => {
    if (viewMode === ViewMode.DATE) {
      const newDate = new Date(displayYear, displayMonth - 1, 1)
      setDisplayYear(newDate.getFullYear())
      setDisplayMonth(newDate.getMonth())
    } else if (viewMode === ViewMode.MONTH) {
      setDisplayYear(displayYear - 1)
    } else if (viewMode === ViewMode.YEAR) {
      setDisplayYear(displayYear - 12)
    }
  }

  const handleNext = (): void => {
    if (viewMode === ViewMode.DATE) {
      const newDate = new Date(displayYear, displayMonth + 1, 1)
      setDisplayYear(newDate.getFullYear())
      setDisplayMonth(newDate.getMonth())
    } else if (viewMode === ViewMode.MONTH) {
      setDisplayYear(displayYear + 1)
    } else if (viewMode === ViewMode.YEAR) {
      setDisplayYear(displayYear + 12)
    }
  }

  const handleTitleClick = (): void => {
    if (viewMode === ViewMode.DATE && pickerType !== DatePickerType.MONTH) {
      setViewMode(ViewMode.MONTH)
    } else if (viewMode === ViewMode.MONTH) {
      setViewMode(ViewMode.YEAR)
    }
  }

  const handleTodayClick = (): void => {
    const today = new Date()
    setCurrentDate(today)
    setDisplayYear(today.getFullYear())
    setDisplayMonth(today.getMonth())

    if (pickerType === DatePickerType.MONTH) {
      const thisMonth = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      setCurrentDate(thisMonth)
      onChange(thisMonth)
      onClose()
    } else {
      setViewMode(ViewMode.DATE)
      setSelectedTime(today.toTimeString().slice(0, 5))

      if (pickerType === DatePickerType.DATETIME) {
        // datetime mode: navigate to today but don't close
      } else {
        onChange(today)
        onClose()
      }
    }
  }

  const renderDateView = (): React.ReactNode => {
    const dateToDisplay = moment().year(displayYear).month(displayMonth).date(1)
    const focusedDate = currentDate ? moment(currentDate) : moment()
    let pcierViewType: string = ViewMode.DATE
    if (pickerType === DatePickerType.WEEK) {
      pcierViewType = ViewMode.WEEK
    } else {
      pcierViewType = ViewMode.DATE
    }

    return (
      <MonthGrid
        date={dateToDisplay.toDate()}
        focusedDate={focusedDate.toDate()}
        viewType={pcierViewType}
        hasEventIndexes={hasEventIndexes as any}
        onDateClick={handleDateClick}
        callViewType={pickerType === DatePickerType.WEEK ? 'calendar' : 'todo'}
        limitDate={limitDate as any}
      />
    )
  }

  const renderMonthView = (): React.ReactNode => {
    return (
      <MonthPicker
        focusedMonth={focusedDate}
        onMonthClick={handleMonthClick}
        inYear={displayYear}
        pickerType="todo"
      />
    )
  }

  const renderYearView = (): React.ReactNode => {
    const startYear = displayYear - 5
    const years: number[] = []
    for (let i = 0; i < 12; i++) {
      years.push(startYear + i)
    }

    const currentYear = new Date().getFullYear()
    const selectedYear = currentDate ? currentDate.getFullYear() : -1

    return (
      <div className={styles.yearGrid}>
        {years.map((year) => {
          const isCurrent = year === currentYear
          const isSelected = year === selectedYear

          return (
            <button
              key={year}
              className={`${styles.yearButton} ${isCurrent ? styles.current : ''} ${
                isSelected ? styles.selected : ''
              }`}
              onClick={() => handleYearClick(year)}
            >
              {year}
            </button>
          )
        })}
      </div>
    )
  }

  const getTitle = (): string => {
    if (viewMode === ViewMode.DATE) {
      const locale = settings.getValue('general').locale
      return `${displayYear} ${new Date(displayYear, displayMonth).toLocaleDateString(locale, { month: 'long' })}`
    } else if (viewMode === ViewMode.MONTH) {
      return `${displayYear}`
    } else if (viewMode === ViewMode.YEAR) {
      const startYear = displayYear - 5
      return `${startYear} - ${startYear + 11}`
    }
    return ''
  }

  return (
    <div
      className={
        navigationContainerType
          ? styles.navigationContainer
          : useDialogStyle
            ? styles.datePickerContainerDialog
            : styles.datePickerContainer
      }
    >
      <div className={styles.header}>
        {!disableExpand && (
          <button className={styles.expandButton} onClick={() => setExpanded(!expanded)}>
            <img
              src={expanded ? chevronCircleDown24SVG : chevronCircleRight24SVG}
              alt="navigation-expand"
            />
          </button>
        )}
        <button className={styles.titleButton} onClick={handleTitleClick}>
          {getTitle()}
        </button>
        <div className={styles.navButtons}>
          <button className={styles.navButton} onClick={handlePrevious}>
            <img src={navigationLeft16SVG} alt="Previous" />
          </button>
          <button className={styles.navButton} onClick={handleNext}>
            <img src={navigationRight16SVG} alt="Next" />
          </button>
        </div>
      </div>

      {expanded && (
        <>
          <div className={useTodayMenu ? styles.content : ''}>
            {viewMode === ViewMode.DATE && pickerType !== DatePickerType.MONTH && renderDateView()}
            {viewMode === ViewMode.MONTH && renderMonthView()}
            {viewMode === ViewMode.YEAR && renderYearView()}
          </div>

          {pickerType === DatePickerType.DATETIME && (
            <div className={styles.timeSection}>
              {useTodayMenu && (
                <button className={styles.todayButtonInTime} onClick={handleTodayClick}>
                  {i18n.t('todo.editor.today')}
                </button>
              )}
              <div className={styles.timeInputWrapper}>
                <CustomTimeSelector
                  timing="start"
                  onTimeChange={handleCustomTimeChange}
                  editEventData={editEventData.current}
                  defaultValue={format12Hours(currentDate, editEventData.current.startTz)}
                />
              </div>
            </div>
          )}

          {useTodayMenu && (
            <div>
              {pickerType === DatePickerType.DATETIME ? (
                <div className={styles.footer}>
                  <div className={styles.right}>
                    <button
                      className={styles.cancelButton}
                      onClick={onClose}
                      style={{ minWidth: 100 }}
                    >
                      {t('common.button.cancel')}
                    </button>
                    <button className={styles.saveButton} onClick={handleDateTimeConfirm}>
                      {t('common.button.save')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.normalFooter}>
                  <button className={styles.todayButton} onClick={handleTodayClick}>
                    {i18n.t('todo.editor.today')}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CalendarDatePicker
